import { createContext, useContext, useState, type ReactNode } from "react";

// ── Tipos ────────────────────────────────────────────────────────────────────

export type PeriodType =
  | "today"
  | "yesterday"
  | "this_week"
  | "last_week"
  | "current_month"
  | "last_month"
  | "current_quarter"
  | "current_year"
  | "year_2026"
  | "custom";

export type ComparisonType =
  | "previous_period"
  | "same_period_last_month"
  | "same_period_last_year"
  | "avg_3months"
  | "avg_12months"
  | "none";

export interface GlobalFilters {
  period: PeriodType;
  comparison: ComparisonType;
  customFrom?: string;  // "YYYY-MM-DD"
  customTo?: string;    // "YYYY-MM-DD" (inclusive — convertida para exclusiva ao chamar API)
  clientId?: string;
  vehicleId?: string;
  driverId?: string;
  bankAccountId?: string;
  costCenterId?: string;
  status?: string;
}

// ── Context ──────────────────────────────────────────────────────────────────

const defaultFilters: GlobalFilters = {
  period: "current_month",
  comparison: "previous_period",
};

interface FiltersContextValue {
  filters: GlobalFilters;
  setFilters: (f: GlobalFilters) => void;
  resetFilters: () => void;
}

const FiltersContext = createContext<FiltersContextValue | null>(null);

export function FiltersProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<GlobalFilters>(defaultFilters);
  return (
    <FiltersContext.Provider value={{ filters, setFilters, resetFilters: () => setFilters(defaultFilters) }}>
      {children}
    </FiltersContext.Provider>
  );
}

export function useFilters() {
  const ctx = useContext(FiltersContext);
  if (!ctx) throw new Error("useFilters must be inside FiltersProvider");
  return ctx;
}

// ── Opções de período ────────────────────────────────────────────────────────

export const PERIOD_OPTIONS: { value: PeriodType; label: string }[] = [
  { value: "today",           label: "Hoje" },
  { value: "yesterday",       label: "Ontem" },
  { value: "this_week",       label: "Esta semana" },
  { value: "last_week",       label: "Semana passada" },
  { value: "current_month",   label: "Este mês" },
  { value: "last_month",      label: "Mês passado" },
  { value: "current_quarter", label: "Este trimestre" },
  { value: "current_year",    label: "Ano atual" },
  { value: "year_2026",       label: "Ano de 2026" },
  { value: "custom",          label: "Período personalizado" },
];

export const COMPARISON_OPTIONS: { value: ComparisonType; label: string }[] = [
  { value: "previous_period",        label: "Período anterior" },
  { value: "same_period_last_month", label: "Mesmo período mês anterior" },
  { value: "same_period_last_year",  label: "Mesmo período ano anterior" },
  { value: "avg_3months",            label: "Média últimos 3 meses" },
  { value: "avg_12months",           label: "Média últimos 12 meses" },
  { value: "none",                   label: "Sem comparação" },
];

export function periodLabel(period: PeriodType): string {
  return PERIOD_OPTIONS.find((o) => o.value === period)?.label ?? period;
}

export function comparisonLabel(comparison: ComparisonType): string {
  return COMPARISON_OPTIONS.find((o) => o.value === comparison)?.label ?? comparison;
}

// ── Cálculo de datas ─────────────────────────────────────────────────────────

/**
 * Meia-noite em São Paulo = 03:00 UTC (UTC-3, sem horário de verão desde 2019).
 * Todos os limites são gerados como UTC com hora 03:00 para ficarem alinhados
 * ao início do dia em SP, independente do timezone do navegador.
 */
const SP_OFFSET_H = 3; // UTC-3 fixo

/** Decompõe um Date em partes de ano/mês/dia no fuso SP. */
function spParts(d: Date): { y: number; m: number; day: number; dow: number } {
  // Desloca o timestamp para o "horário SP" e lê em UTC
  const sp = new Date(d.getTime() - SP_OFFSET_H * 3600_000);
  const dow = new Date(Date.UTC(sp.getUTCFullYear(), sp.getUTCMonth(), sp.getUTCDate())).getDay();
  return {
    y:   sp.getUTCFullYear(),
    m:   sp.getUTCMonth(),    // 0-indexed
    day: sp.getUTCDate(),
    dow,                       // 0=dom, 1=seg, …, 6=sáb
  };
}

/** Meia-noite SP de um dia dado em partes. */
function midnight(y: number, m: number, day: number): Date {
  return new Date(Date.UTC(y, m, day, SP_OFFSET_H, 0, 0, 0));
}

/**
 * Converte PeriodType em { from, to } como ISO strings prontas para a API.
 *
 * Convenção: from inclusivo, to exclusivo — ex: junho/2026 =
 *   from = "2026-06-01T03:00:00.000Z"  (meia-noite SP de 01/06)
 *   to   = "2026-07-01T03:00:00.000Z"  (meia-noite SP de 01/07)
 */
export function periodToDates(
  period: PeriodType,
  customFrom?: string,
  customTo?: string,
): { from: string; to: string } {
  const now = new Date();
  const { y, m, day, dow } = spParts(now);

  switch (period) {
    case "today": {
      const f = midnight(y, m, day);
      return { from: f.toISOString(), to: midnight(y, m, day + 1).toISOString() };
    }

    case "yesterday": {
      const f = midnight(y, m, day - 1);
      return { from: f.toISOString(), to: midnight(y, m, day).toISOString() };
    }

    case "this_week": {
      // Semana começa na segunda-feira (ISO 8601)
      const daysFromMon = dow === 0 ? 6 : dow - 1;
      const mon = midnight(y, m, day - daysFromMon);
      return { from: mon.toISOString(), to: new Date(mon.getTime() + 7 * 86400_000).toISOString() };
    }

    case "last_week": {
      const daysFromMon = dow === 0 ? 6 : dow - 1;
      const thisMon = midnight(y, m, day - daysFromMon);
      const lastMon = new Date(thisMon.getTime() - 7 * 86400_000);
      return { from: lastMon.toISOString(), to: thisMon.toISOString() };
    }

    case "current_month":
      // MTD: do primeiro dia do mês até hoje (inclusive) — não o mês inteiro futuro
      return {
        from: midnight(y, m, 1).toISOString(),
        to:   midnight(y, m, day + 1).toISOString(),
      };

    case "last_month":
      return {
        from: midnight(y, m - 1, 1).toISOString(),
        to:   midnight(y, m, 1).toISOString(),
      };

    case "current_quarter": {
      // QTD: do início do trimestre até hoje (inclusive)
      const q = Math.floor(m / 3);
      return {
        from: midnight(y, q * 3, 1).toISOString(),
        to:   midnight(y, m, day + 1).toISOString(),
      };
    }

    case "current_year":
      // YTD: de 1º/Jan até hoje (inclusive)
      return {
        from: midnight(y, 0, 1).toISOString(),
        to:   midnight(y, m, day + 1).toISOString(),
      };

    case "year_2026":
      return {
        from: midnight(2026, 0, 1).toISOString(),
        to:   midnight(2027, 0, 1).toISOString(),
      };

    case "custom": {
      if (customFrom && customTo) {
        // customFrom/To são "YYYY-MM-DD" (input type=date).
        // Convertemos para meia-noite SP. O "to" é o início do DIA SEGUINTE
        // para garantir que o dia final seja incluído (to exclusivo).
        const [fy, fm, fd] = customFrom.split("-").map(Number) as [number, number, number];
        const [ty, tm, td] = customTo.split("-").map(Number) as [number, number, number];
        return {
          from: midnight(fy, fm - 1, fd).toISOString(),
          to:   midnight(ty, tm - 1, td + 1).toISOString(), // dia seguinte = to exclusivo
        };
      }
      // fallback: mês atual
      return {
        from: midnight(y, m, 1).toISOString(),
        to:   midnight(y, m + 1, 1).toISOString(),
      };
    }
  }
}
